


import { StickerTrayCanvasDrawer } from "../StickerTrayCanvasDrawer";
import { ImageStore } from "../ImageStore";
import { useEffect, useRef, useState } from "react";
import { CanvasImage } from "../CanvasImage";
import { CanvasDrawer } from "../CanvasDrawer";
import { screenToCanvas } from "../ScreenCordToCanvasCord";
import { useStickerSectionStore } from "../EditorStore";
import { UndoRedoHelper } from "../UndoRedoHelper";
import { NamedImage } from "../ImageStore";
import localforage from "localforage";
import { resetCanvas } from "../ResetCanvas";
import { FullscreenModal } from "./NavBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { isHeicFile, convertHeicToPng } from "../utils/heicConversion";
import { removeBackgroundLocal as removeBackground, preloadModel } from "../utils/removeBackgroundLocal";
import { requestNotificationPermission, notifyImagesReady } from "../utils/notifications";
import { resizeForUpload } from "../utils/resizeForUpload";
import { trimTransparentBounds } from "../utils/trimTransparentBounds";
import { applyOrganicEdge } from "../utils/organicEdge";
import { trybeTrack } from "../utils/trybeTracking";

export default function StickerTray() {
    const lastTouchPosition = useRef<null | number>(null);
    const lastTouchTime = useRef<null | number>(null);
    const velocity = useRef<null | number>(null);
    const velocityHistory = useRef<number[]>([]);

    const firstTouchPosition = useRef<number | null>(null)
    const firstTouchTimer = useRef<NodeJS.Timeout | null>(null)
    const draggingImage = useRef<boolean>(false)
    const namedImageRef = useRef<NamedImage | null>(null)

    const dragCanvasRef = useRef<null | HTMLCanvasElement>(null)
    const dragCanvasCtxRef = useRef<null | CanvasRenderingContext2D>(null)
    const maxStickerSectionHeight = useRef<number>(0)
    const stickerTrayRef = useRef<HTMLDivElement | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const setCanvasImageSelected = useStickerSectionStore((state) => state.setCanvasImageSelected);
    const currentSelectedCanvasImageRef = useStickerSectionStore((state) => state.currentCanvasImageSelectedRef)
    const setShowStickersSection = useStickerSectionStore((state) => state.setShowStickersSection)

    const setUndoRedo = useStickerSectionStore((state) => state.setUndoRedo)

    const [showResetStickerTrayModal, setShowResetStickerTrayModal] = useState(false)
    const [showHowToMakeStickersModal, setShowHowToMakeStickersModal] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<{ done: number, total: number } | null>(null)

    // The background-removal model (onnxruntime-web, WASM) is loaded lazily
    // the first time it's needed - without a preload, that cold-start cost
    // (downloading + compiling the model) would land silently on the user's
    // first upload. Kick it off as soon as the tray mounts instead, and keep
    // the upload button disabled/loading until it's warm so there's no
    // surprise delay later.
    const [isModelReady, setIsModelReady] = useState(false)
    // Tracks whether the user has ever clicked the upload button, purely to
    // drive the "look here" pulsing highlight once the model finishes
    // loading - the pulse stops the moment they first interact with it.
    const [hasClickedUpload, setHasClickedUpload] = useState(false)

    useEffect(() => {
        preloadModel("medium")
            .catch((err) => console.error("Failed to preload background-removal model:", err))
            .finally(() => setIsModelReady(true));
    }, []);

    const clearStickerTray = async () => {
        const imageResponseIds = (await localforage.getItem<string[]>('images')) ?? [];

        if (imageResponseIds.length === 0) {
            console.log('No cached images found.');
            return;
        }

        const cache = await caches.open(ImageStore.cacheName);

        for (const imgId of imageResponseIds) {
            await cache.delete(imgId);
        }

        await localforage.removeItem("images")

        ImageStore.namedImages.length = 0
        StickerTrayCanvasDrawer.imagesBounds = []
        StickerTrayCanvasDrawer.offsetX = 0
        StickerTrayCanvasDrawer.startX = 20

        StickerTrayCanvasDrawer.redrawStickerTrayCanvas()

        await resetCanvas()

    }

    const clearCanvasConformationModalChild = () => {
        return (
            <div onClick={() => setShowResetStickerTrayModal(false)}
                style={{ height: "100%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <div style={{
                    height: "20%",
                    width: "60%",
                    backgroundColor: "#6b9e9e",
                    borderRadius: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "20px",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    color: "#ffffff"
                }}>
                    <div style={{ fontSize: "16px", fontWeight: 500, textAlign: "center" }}>
                        Are you sure you want to clear the sticker tray? This will also clear the canvas.
                    </div>
                    <div style={{ display: "flex", gap: "16px" }}>
                        <button onClick={() => setShowResetStickerTrayModal(false)} style={{
                            padding: "8px 16px",
                            backgroundColor: "#5a7a7a",
                            borderRadius: "10px",
                            border: "none",
                            fontWeight: 500,
                            color: "#ffffff",
                            WebkitTapHighlightColor: "transparent",
                        }}>
                            No
                        </button>
                        <button
                            onClick={clearStickerTray}
                            style={{
                                padding: "8px 16px",
                                backgroundColor: "#3d5a5a",
                                color: "#ffffff",
                                borderRadius: "10px",
                                border: "none",
                                fontWeight: 500,
                                WebkitTapHighlightColor: "transparent",
                            }}>
                            Yes
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const howToMakeStickersModalChild = () => {
        return (
            <div onClick={() => setShowHowToMakeStickersModal(false)}
                style={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.6)"
                }}>
                <div style={{
                    position: "relative",
                    width: "70%",
                    maxWidth: "600px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }} onClick={(e) => e.stopPropagation()}>
                    <div style={{
                        pointerEvents: "none",
                        position: "absolute",
                        top: "-30px",
                        right: "0px",
                        width: "30px",
                        height: "30px",
                        color: "#ffffff",
                        fontSize: "24px",
                        fontWeight: "bold",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer"
                    }}>
                        ×
                    </div>
                    <video
                        src="/how-to-make-stickers.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls
                        style={{
                            width: "100%",
                            borderRadius: "15px",
                            boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
                        }}
                    />
                </div>
            </div>
        )
    }

    const updateUndoRedo = () => {


        const dict = { canUndo: false, canRedo: false }

        if (UndoRedoHelper.undoStack.length > 0) {
            dict.canUndo = true
        }

        setUndoRedo(dict)
    }

    const initializeStickerTrayCanvasDrawer = (canvas: HTMLCanvasElement | null) => {
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (ctx) {
            const dpr = window.devicePixelRatio || 1;

            canvas.width = canvas.offsetWidth * dpr;
            canvas.height = canvas.offsetHeight * dpr;

            canvas.style.width = `${canvas.offsetWidth}px`;
            canvas.style.height = `${canvas.offsetHeight}px`;

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            const stickerSectionContainer = canvas.parentElement;
            const currentHeight = stickerSectionContainer?.clientHeight ?? canvas.clientHeight;

            if (currentHeight > maxStickerSectionHeight.current) {
                maxStickerSectionHeight.current = currentHeight;
            }

            const stickerTrayStickerSectionHeight = currentHeight > 10
                ? currentHeight
                : maxStickerSectionHeight.current;

            StickerTrayCanvasDrawer.initialize(canvas, ctx, dpr, stickerTrayStickerSectionHeight);
            StickerTrayCanvasDrawer.namedImages = ImageStore.namedImages;
            StickerTrayCanvasDrawer.redrawStickerTrayCanvas();
        }
    };

    const initializeImageDragLayerCanvas = (canvas: HTMLCanvasElement) => {
        if (!canvas) return;
        dragCanvasRef.current = canvas;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            const dpr = window.devicePixelRatio || 1;

            canvas.width = window.innerWidth * dpr;
            canvas.height = window.innerHeight * dpr;

            canvas.style.width = `${window.innerWidth}px`;
            canvas.style.height = `${window.innerHeight}px`;

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            dragCanvasCtxRef.current = ctx;
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        (document.activeElement as HTMLElement)?.blur();

        setCanvasImageSelected(false)
        if (currentSelectedCanvasImageRef.current) {
            currentSelectedCanvasImageRef.current.selected = false
            currentSelectedCanvasImageRef.current = null
            draggingImage.current = false
        }

        CanvasDrawer.rerenderCanvas()

        const touches = e.touches;
        if (touches.length === 1) {
            lastTouchPosition.current = touches[0].clientX;
            lastTouchTime.current = Date.now();
            velocity.current = 0;
            velocityHistory.current = [];
            firstTouchPosition.current = e.touches[0].clientX
            firstTouchTimer.current = setTimeout(() => {
                draggingImage.current = true
                if (dragCanvasCtxRef.current) {

                    const ctx = dragCanvasCtxRef.current;
                    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

                    const canvasXTouchPosition = e.touches[0].clientX - StickerTrayCanvasDrawer.offsetX

                    const bounds = StickerTrayCanvasDrawer.imagesBounds

                    let namedImage

                    for (let i = 0; i < bounds.length; i++) {
                        const bound = bounds[i]
                        if (bound.leftBound < canvasXTouchPosition && bound.rightBound > canvasXTouchPosition) {
                            namedImage = StickerTrayCanvasDrawer.namedImages[i]
                            namedImageRef.current = namedImage
                            setShowStickersSection(false)
                        }
                    }
                    if (namedImage)
                        ctx.drawImage(namedImage.img, e.touches[0].clientX - namedImage.img.width / 16, e.touches[0].clientY - namedImage.img.height / 16, namedImage.img.width / 8, namedImage.img.height / 8);
                }
            }, 200)

        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {

        const touches = e.touches;

        if (draggingImage.current) {
            if (dragCanvasCtxRef.current) {
                const ctx = dragCanvasCtxRef.current;
                ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

                if (namedImageRef.current) {
                    ctx.drawImage(namedImageRef.current.img, e.touches[0].clientX - namedImageRef.current.img.width / 16, e.touches[0].clientY - namedImageRef.current.img.height / 16, namedImageRef.current.img.width / 8, namedImageRef.current.img.height / 8);
                }
            }
        } else {
            if (touches.length === 1 && lastTouchPosition.current !== null) {
                if (firstTouchTimer.current) {
                    clearTimeout(firstTouchTimer.current);
                    firstTouchTimer.current = null;
                }
                const dx = touches[0].clientX - lastTouchPosition.current;
                const now = Date.now();
                const timeElapsed = now - (lastTouchTime.current || now);

                const newVelocity = dx / timeElapsed;

                velocityHistory.current.push(newVelocity);

                if (velocityHistory.current.length > 10) {
                    velocityHistory.current.shift();
                }

                const avgVelocity = velocityHistory.current.reduce((sum, val) => sum + val, 0) / velocityHistory.current.length;
                velocity.current = avgVelocity * 1.8;

                lastTouchPosition.current = touches[0].clientX;
                lastTouchTime.current = now;

                if (StickerTrayCanvasDrawer.offsetX + dx < 0 && -(StickerTrayCanvasDrawer.startX - StickerTrayCanvasDrawer.canvas!.offsetWidth) < StickerTrayCanvasDrawer.offsetX + dx) {
                    StickerTrayCanvasDrawer.offsetX += dx;
                    StickerTrayCanvasDrawer.redrawStickerTrayCanvas();
                }
            }
        }

    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (dragCanvasCtxRef.current && draggingImage.current) {
            setShowStickersSection(true)
            draggingImage.current = false;
            const canvas = dragCanvasCtxRef.current.canvas;
            const ctx = dragCanvasCtxRef.current;
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            const { x: lastTouchX, y: lastTouchY } = screenToCanvas(e, canvas);

            if (namedImageRef.current) {

                const scaledWidth = namedImageRef.current.img.width / CanvasDrawer.scale / 8;
                const scaledHeight = namedImageRef.current.img.height / CanvasDrawer.scale / 8;

                const centerX = lastTouchX - scaledWidth / 2;
                const centerY = lastTouchY - scaledHeight / 2;

                const BR_imageBounds = {
                    x: centerX + scaledWidth,
                    y: centerY + scaledHeight
                };



                const index = CanvasDrawer.canvasImages.length

                const image = new CanvasImage(
                    namedImageRef.current.imgID,
                    index,
                    { x: centerX, y: centerY },
                    BR_imageBounds
                );

                CanvasDrawer.canvasImages.push(image);
                CanvasDrawer.allocateGhostArrayImages()
                CanvasDrawer.rerenderCanvas();
                UndoRedoHelper.createSnapShot()
                updateUndoRedo()
            }
        }
        if (velocity.current && Math.abs(velocity.current) > 0.05) {
            const deceleration = 0.88;
            let lastKnownVelocity = velocity.current;

            const inertiaScroll = () => {
                if (Math.abs(lastKnownVelocity) < 0.01 || StickerTrayCanvasDrawer.offsetX + lastKnownVelocity > 0 || -(StickerTrayCanvasDrawer.startX - StickerTrayCanvasDrawer.canvas!.offsetWidth) > StickerTrayCanvasDrawer.offsetX + lastKnownVelocity) return;

                StickerTrayCanvasDrawer.offsetX += lastKnownVelocity;
                lastKnownVelocity *= deceleration;
                StickerTrayCanvasDrawer.redrawStickerTrayCanvas();
                requestAnimationFrame(inertiaScroll);
            };

            inertiaScroll();
        }

        lastTouchPosition.current = null;
        velocity.current = null;
        velocityHistory.current = [];
    };

    const globalMouseMove = (e: MouseEvent) => {
        if (draggingImage.current) {
            if (dragCanvasCtxRef.current) {
                const ctx = dragCanvasCtxRef.current;
                ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

                if (namedImageRef.current) {
                    ctx.drawImage(namedImageRef.current.img, e.clientX - namedImageRef.current.img.width / 16, e.clientY - namedImageRef.current.img.height / 16, namedImageRef.current.img.width / 8, namedImageRef.current.img.height / 8);
                }
            }
        } else {
            if (lastTouchPosition.current !== null) {
                if (firstTouchTimer.current) {
                    clearTimeout(firstTouchTimer.current);
                    firstTouchTimer.current = null;
                }
                const dx = e.clientX - lastTouchPosition.current;
                const now = Date.now();
                const timeElapsed = now - (lastTouchTime.current || now);

                const newVelocity = dx / timeElapsed;

                velocityHistory.current.push(newVelocity);

                if (velocityHistory.current.length > 10) {
                    velocityHistory.current.shift();
                }

                const avgVelocity = velocityHistory.current.reduce((sum, val) => sum + val, 0) / velocityHistory.current.length;
                velocity.current = avgVelocity * 1.8;

                lastTouchPosition.current = e.clientX;
                lastTouchTime.current = now;

                if (StickerTrayCanvasDrawer.offsetX + dx < 0 && -(StickerTrayCanvasDrawer.startX - StickerTrayCanvasDrawer.canvas!.offsetWidth) < StickerTrayCanvasDrawer.offsetX + dx) {
                    StickerTrayCanvasDrawer.offsetX += dx;
                    StickerTrayCanvasDrawer.redrawStickerTrayCanvas();
                }
            }
        }
    };

    const globalMouseUp = (e: MouseEvent) => {
        window.removeEventListener('mousemove', globalMouseMove);
        window.removeEventListener('mouseup', globalMouseUp);

        if (dragCanvasCtxRef.current && draggingImage.current) {
            setShowStickersSection(true)
            draggingImage.current = false;
            const canvas = dragCanvasCtxRef.current.canvas;
            const ctx = dragCanvasCtxRef.current;
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            const fakeEvent = {
                clientX: e.clientX,
                clientY: e.clientY
            } as React.MouseEvent;

            const { x: lastTouchX, y: lastTouchY } = screenToCanvas(fakeEvent, canvas);

            if (namedImageRef.current) {

                const scaledWidth = namedImageRef.current.img.width / CanvasDrawer.scale / 8;
                const scaledHeight = namedImageRef.current.img.height / CanvasDrawer.scale / 8;

                const centerX = lastTouchX - scaledWidth / 2;
                const centerY = lastTouchY - scaledHeight / 2;

                const BR_imageBounds = {
                    x: centerX + scaledWidth,
                    y: centerY + scaledHeight
                };



                const index = CanvasDrawer.canvasImages.length

                const image = new CanvasImage(
                    namedImageRef.current.imgID,
                    index,
                    { x: centerX, y: centerY },
                    BR_imageBounds
                );

                CanvasDrawer.canvasImages.push(image);
                CanvasDrawer.allocateGhostArrayImages()
                CanvasDrawer.rerenderCanvas();
                UndoRedoHelper.createSnapShot()
                updateUndoRedo()
            }
        }
        if (velocity.current && Math.abs(velocity.current) > 0.05) {
            const deceleration = 0.88;
            let lastKnownVelocity = velocity.current;

            const inertiaScroll = () => {
                if (Math.abs(lastKnownVelocity) < 0.01 || StickerTrayCanvasDrawer.offsetX + lastKnownVelocity > 0 || -(StickerTrayCanvasDrawer.startX - StickerTrayCanvasDrawer.canvas!.offsetWidth) > StickerTrayCanvasDrawer.offsetX + lastKnownVelocity) return;

                StickerTrayCanvasDrawer.offsetX += lastKnownVelocity;
                lastKnownVelocity *= deceleration;
                StickerTrayCanvasDrawer.redrawStickerTrayCanvas();
                requestAnimationFrame(inertiaScroll);
            };

            inertiaScroll();
        }

        lastTouchPosition.current = null;
        velocity.current = null;
        velocityHistory.current = [];
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setCanvasImageSelected(false)
        if (currentSelectedCanvasImageRef.current) {
            currentSelectedCanvasImageRef.current.selected = false
            currentSelectedCanvasImageRef.current = null
            draggingImage.current = false
        }

        CanvasDrawer.rerenderCanvas()

        lastTouchPosition.current = e.clientX;
        lastTouchTime.current = Date.now();
        velocity.current = 0;
        velocityHistory.current = [];
        firstTouchPosition.current = e.clientX
        firstTouchTimer.current = setTimeout(() => {
            draggingImage.current = true
            if (dragCanvasCtxRef.current) {

                const ctx = dragCanvasCtxRef.current;
                ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

                const canvasXTouchPosition = e.clientX - StickerTrayCanvasDrawer.offsetX

                const bounds = StickerTrayCanvasDrawer.imagesBounds

                let namedImage

                for (let i = 0; i < bounds.length; i++) {
                    const bound = bounds[i]
                    if (bound.leftBound < canvasXTouchPosition && bound.rightBound > canvasXTouchPosition) {
                        namedImage = StickerTrayCanvasDrawer.namedImages[i]
                        namedImageRef.current = namedImage
                        setShowStickersSection(false)
                    }
                }
                if (namedImage)
                    ctx.drawImage(namedImage.img, e.clientX - namedImage.img.width / 16, e.clientY - namedImage.img.height / 16, namedImage.img.width / 8, namedImage.img.height / 8);
            }
        }, 200)

        window.addEventListener('mousemove', globalMouseMove);
        window.addEventListener('mouseup', globalMouseUp);
    };

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', globalMouseMove);
            window.removeEventListener('mouseup', globalMouseUp);
            if (firstTouchTimer.current) {
                clearTimeout(firstTouchTimer.current);
            }
        };
    }, []);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = "";

        if (files.length === 0) return;

        requestNotificationPermission();
        setIsUploading(true);
        setUploadProgress({ done: 0, total: files.length });

        // Sequential, not parallel: keeps progress reporting ("Processing
        // 2/5...") simple and avoids hammering the WASM background-removal
        // model with concurrent inference calls.
        let completedCount = 0;
        let successCount = 0;

        for (const file of files) {
            try {
                let sourceFile = file;

                if (isHeicFile(file)) {
                    const pngBlob = await convertHeicToPng(file);
                    sourceFile = new File(
                        [pngBlob],
                        file.name.replace(/\.(heic|heif)$/i, ".png"),
                        { type: "image/png" }
                    );
                }

                const resizedFile = await resizeForUpload(sourceFile);
                const cutoutBlob = await removeBackground(resizedFile, "medium");
                const cutoutFile = new File(
                    [cutoutBlob],
                    sourceFile.name.replace(/\.[^.]+$/, ".png"),
                    { type: "image/png" }
                );

                const trimmedFile = await trimTransparentBounds(cutoutFile);
                const smoothedFile = await applyOrganicEdge(trimmedFile);

                // resizedFile is the pre-background-removal source (full
                // photo, real background) - saved alongside the cutout so
                // checkout can later redo the removal with a better model.
                await ImageStore.addImage(smoothedFile, resizedFile);
                successCount += 1;
            } catch (err) {
                console.error("Failed to process uploaded image:", err);
            } finally {
                completedCount += 1;
                setUploadProgress({ done: completedCount, total: files.length });
            }
        }

        setIsUploading(false);
        setUploadProgress(null);
        notifyImagesReady();

        if (successCount > 0) {
            trybeTrack("images_added_to_sticker_tray", { count: successCount });
        }
    };

    const stickerTrayButtonStyles = {
        marginRight: "2%", width: "5%", height: "100%", borderRadius: "10px", background: "none", color: "white", border: "1px solid white", marginLeft: "2%"
    }

    return (
        <>
            <div
                ref={stickerTrayRef}
                style={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "transparent",
                    borderTop: "1px solid white",
                    pointerEvents: "auto",
                }}
            >
                <div style={{ display: "flex", flexDirection: "row", height: "20%", justifyContent: "space-between", gap: "2%", width: "100%" }}>
                    <div style={{ height: "100%", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "1%", paddingBottom: "1%" }}>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFileUpload}
                        />

                        <button
                            onClick={() => {
                                setHasClickedUpload(true);
                                fileInputRef.current?.click();
                            }}
                            disabled={isUploading || !isModelReady}
                            className={isModelReady && !isUploading && !hasClickedUpload ? "add-sticker-pulse" : undefined}
                            style={{
                                ...stickerTrayButtonStyles,
                                width: "20vw",
                                textAlign: "center",
                                cursor: isUploading || !isModelReady ? "default" : "pointer",
                                opacity: isUploading || !isModelReady ? 0.6 : 1,
                            }}
                        >
                            {!isModelReady
                                ? "Loading…"
                                : isUploading
                                    ? (uploadProgress ? `Processing ${uploadProgress.done}/${uploadProgress.total}…` : "Uploading…")
                                    : "Create Sticker"}
                        </button>
                        <style>{`
                            @keyframes addStickerPulse {
                                0%, 100% {
                                    box-shadow: 0 0 0 0 rgba(107, 158, 158, 0.9);
                                    border-color: #6b9e9e;
                                }
                                50% {
                                    box-shadow: 0 0 10px 6px rgba(107, 158, 158, 0);
                                    border-color: #ffffff;
                                }
                            }
                            .add-sticker-pulse {
                                animation: addStickerPulse 1.6s ease-in-out infinite;
                            }
                        `}</style>

                        <button
                            onClick={() => setShowHowToMakeStickersModal(true)}
                            style={{
                                ...stickerTrayButtonStyles,
                                width: "35vw",
                                fontSize: "12px",
                                whiteSpace: "nowrap"
                            }}
                        >
                            How to Make Stickers
                        </button>

                        <button onClick={() => setShowResetStickerTrayModal(true)} style={{ ...stickerTrayButtonStyles, width: "20vw" }} >
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                    </div>
                </div>


                {showResetStickerTrayModal && <FullscreenModal children={clearCanvasConformationModalChild()} />}
                {showHowToMakeStickersModal && <FullscreenModal children={howToMakeStickersModalChild()} />}

                <div
                    style={{
                        display: "flex",
                        height: "80%",
                        width: "100%",
                    }}
                >
                    <canvas
                        ref={initializeStickerTrayCanvasDrawer}
                        style={{
                            height: "100%", width: "100%", WebkitUserSelect: "none",
                            userSelect: "none",
                            WebkitTouchCallout: "none",
                            WebkitTapHighlightColor: "transparent",
                            touchAction: "manipulation",
                            backgroundColor: "transparent",
                            paddingTop: "1%",
                            paddingBottom: "1%"
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                    />
                </div>


                <canvas
                    ref={initializeImageDragLayerCanvas}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        zIndex: 999999999999,
                        pointerEvents: "none",
                        width: "100vw",
                        height: "100vh",
                    }}
                />
            </div>
        </>
    );
}